import CommonSwitch from "@/components/utils/CommonSwitch";
import LoadingDotsAnimated from "@/components/utils/LoadingDotsAnimated";
import SearchField from "@/components/utils/SearchField";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { useControlNavbarColor } from "@/hooks/useControlNavbarColor";
import { useCurrentUserFlags } from "@/hooks/useCurrentUserFlags";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import ChatUserDataDTO from "@/models/chat/ChatUserDataDTO";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { Box, Button, Divider, SxProps, Tooltip, Typography, styled } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { EmptyChatList } from "../empty-chat-components/EmptyChatList";
import { LazoPortalNewChatButton } from "./LazoPortalNewChatButton";
import { PortalChatListFetchMoreEvent, PortalFilters } from "./PortalChatContext";
import { PortalChatListFilters } from "./PortalChatListFilters";
import PortalChatListItem, { CONVERSATION_LIST_DATES_WIDTH } from "./PortalChatListItem";
import { PortalCreateChatModal } from "./PortalCreateChatModal";
import { PortalInboxStatisticsModal } from "./PortalInboxStatistics";

const debouncer = new Debouncer(300);

export const PortalChatList = ({
  chats,
  setChats,
  onChatSelect,
  userTyping,
  selectedChat,
  privateChat,
  setPrivateChat,
  groupChat,
  setGroupChat,
  step,
  setStep,
  loadingPage,
  firstLoad,
  filters,
  setFilters,
}: {
  chats: LazoChatConversationShell[];
  setChats: (chats: LazoChatConversationShell[]) => void;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  userTyping: LazoChatTypingDTO;
  selectedChat: LazoChatConversationShell;
  privateChat: boolean;
  setPrivateChat: (val: boolean) => void;
  groupChat: boolean;
  setGroupChat: (val: boolean) => void;
  step: number;
  setStep: (val: number) => void;
  loadingPage: boolean;
  firstLoad: boolean;
  filters: PortalFilters;
  setFilters: Dispatch<SetStateAction<PortalFilters>>;
}) => {
  const lastChatListItemRef = useRef<HTMLDivElement | null>(null);
  const [statisticsModal, setStatisticsModal] = useState<boolean>(false);
  const { isMobile, isTablet } = useResponsive();

  const openCreateModal = privateChat || groupChat;

  const { currentTenantMembership } = useTenantMemberships();
  const { isTeamAdmin, isAppAdmin } = useCurrentUserFlags();
  const canSeeStats = isTeamAdmin || isAppAdmin;

  // Control navbar background color
  useControlNavbarColor(Colors.WHITE);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          document.dispatchEvent(new CustomEvent(PortalChatListFetchMoreEvent));
          observer.unobserve(lastChatListItemRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (lastChatListItemRef.current) {
      observer.observe(lastChatListItemRef.current);
    }

    return () => {
      if (lastChatListItemRef.current) {
        observer.unobserve(lastChatListItemRef.current);
      }
    };
  }, [chats]);

  function handleSearchChats(searchRef: React.RefObject<HTMLInputElement>) {
    const lowerCaseSearchValue = searchRef.current?.value.toLowerCase() || "";
    setFilters((prev) => ({ ...prev, searchValue: lowerCaseSearchValue || null }));
  }

  function addToChats(c: LazoChatConversationShell) {
    if (!chats.find((current) => current.externalId === c.externalId)) {
      setChats([c, ...chats]);
    }
  }

  function onStartPrivateChat(user: ChatUserDataDTO, tenantExternalId: string) {
    LazoChatConversationService.createPrivateChat(user.externalId, tenantExternalId)
      .then((response) => {
        response.onSuccess((chat) => {
          setPrivateChat(false);
          setStep(0);
          const shell = mapToShell(chat);

          if (chat.internalChannelExternalId) {
            shell.currentThreadStatus = LazoThreadConversationStatus.OPEN;
            shell.currentThreadStarted = new Date();
            shell.lastOpenTime = new Date();
          }

          onChatSelect(shell);
          addToChats(shell);
        });
      })
      .catch((error) => {
        NotificationService.error(error);
      });
  }

  async function handleAllUnreadMessages() {
    let unreadMessages = 0;
    chats.forEach((chat) => (unreadMessages += chat.unreadMessagesCount));
  }

  useEffect(() => {
    handleAllUnreadMessages();
  }, []);

  function onStartGroupChat(users: string[], groupName: string) {
    LazoChatConversationService.createPrivateGroupChat(users, groupName, currentTenantMembership.tenant.externalId!)
      .then((res): void => {
        setStep(0);
        setGroupChat(false);
        const shell = mapToShell(res.getContentOrThrowError());

        if (shell.internalChannelExternalId) {
          shell.currentThreadStatus = LazoThreadConversationStatus.OPEN;
          shell.currentThreadStarted = new Date();
          shell.lastOpenTime = new Date();
        }

        addToChats(shell);
        onChatSelect(shell);
      })
      .catch((error) => {
        NotificationService.error(error);
        console.error("There was an error creating group chat", error);
      });
  }

  function onToggleClientsView() {
    setFilters((prev) => ({ ...prev, otherClients: !prev.otherClients }));
  }

  function onToggleShowResponseTimes() {
    setFilters((prev) => ({ ...prev, showResponseTimes: !prev.showResponseTimes }));
  }

  function onPrivateChatClick() {
    setPrivateChat(true);
  }

  function onGroupChatClick() {
    setGroupChat(true);
  }

  function onCloseCreateChatModal() {
    setPrivateChat(false);
    setGroupChat(false);
    setStep(0);
  }

  return (
    <ChatListContainer isPortal={true}>
      <ToolBar>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: { xs: "auto", sm: "2rem" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "0.5rem", sm: 0 },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: "0.5rem", sm: "0.2rem" }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
            }}
          >
            <SegmentedControl>
              <SegmentOption disableRipple selected={!filters.otherClients} onClick={onToggleClientsView}>
                My clients
              </SegmentOption>
              <SegmentOption disableRipple selected={filters.otherClients} onClick={onToggleClientsView}>
                Others
              </SegmentOption>
            </SegmentedControl>
            {canSeeStats && (
              <StyledIconButton disableRipple onClick={() => setStatisticsModal(true)}>
                Stats
              </StyledIconButton>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: "0.5rem", sm: "1rem" }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-end" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {!isMobile && (
              <Box display="flex" alignItems="center" gap="0.75rem">
                <Tooltip title={"Show conversation threads response times"}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="0.25rem"
                    sx={{ border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`, borderRadius: "50px" }}
                  >
                    <Typography fontSize="0.75rem" sx={{ color: Colors.DARKER_DARKER_GRAY }}>
                      Times
                    </Typography>
                    <CommonSwitch
                      checked={filters.showResponseTimes}
                      onClick={onToggleShowResponseTimes}
                      sx={{
                        height: "1rem",
                        width: "2rem",
                        "& .MuiSwitch-thumb": {
                          height: "0.75rem",
                          width: "0.75rem",
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
                <PortalChatListFilters filters={filters} setFilters={setFilters} />
              </Box>
            )}
            {!isMobile && <Divider sx={{ backgroundColor: Colors.LIGHT_GRAY_DEDEE1, width: "2px", height: "14px" }} />}
            <Tooltip title={"Create new chat"}>
              <Box sx={{ minHeight: { xs: "44px", sm: "auto" } }}>
                <LazoPortalNewChatButton onPrivateChatSelect={onPrivateChatClick} onGroupChatSelect={onGroupChatClick} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <SearchField
          handleSearch={(e) => handleSearchChats(e)}
          displaySearchButton={false}
          debouncer={debouncer}
          triggerSearchOnInputChange
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: { xs: "3rem", sm: "2.5rem" },
              width: "100%",
              margin: { xs: "0.25rem 0", sm: "0.5rem 0" },
            },
            "& .MuiOutlinedInput-input": {
              padding: { xs: "12px", sm: "10px" },
              fontSize: { xs: "1rem", sm: "0.875rem" },
            },
          }}
        />
      </ToolBar>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding={{ xs: "0.375rem 0.5rem", sm: "0.375rem 1rem" }}
        gap="0.5rem"
      >
        <Box display="flex" alignItems="center" gap="0.5rem">
          <Typography
            fontSize="0.625rem"
            fontWeight={600}
            letterSpacing="0.08em"
            sx={{ textTransform: "uppercase", color: Colors.LIGHT_GRAY_8A8E93 }}
          >
            Conversations
          </Typography>
        </Box>
        {!isMobile && (
          <Box display="flex" alignItems="center" justifyContent="center" gap="0.5rem">
            {filters.showResponseTimes && (
              <>
                <Tooltip
                  title={"Time since conversation thread was Opened"}
                  sx={{ borderRadius: "100px", backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE }}
                >
                  <Typography
                    textAlign="center"
                    fontSize="0.75rem"
                    minWidth={CONVERSATION_LIST_DATES_WIDTH}
                    maxWidth={CONVERSATION_LIST_DATES_WIDTH}
                  >
                    O
                  </Typography>
                </Tooltip>
                <Tooltip
                  title={"Time with no Answer"}
                  sx={{ borderRadius: "100px", backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE }}
                >
                  <Typography
                    textAlign="center"
                    fontSize="0.75rem"
                    minWidth={CONVERSATION_LIST_DATES_WIDTH}
                    maxWidth={CONVERSATION_LIST_DATES_WIDTH}
                  >
                    A
                  </Typography>
                </Tooltip>
              </>
            )}
            <Tooltip title={"Last message date"} sx={{ borderRadius: "100px", backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE }}>
              <Typography
                textAlign="center"
                fontSize="0.75rem"
                minWidth={CONVERSATION_LIST_DATES_WIDTH}
                maxWidth={CONVERSATION_LIST_DATES_WIDTH}
              >
                L
              </Typography>
            </Tooltip>
          </Box>
        )}
      </Box>
      <StyledDivider />
      {firstLoad && (
        <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
          <LoadingDotsAnimated />
        </Box>
      )}

      <PortalInboxStatisticsModal open={statisticsModal} onClose={() => setStatisticsModal(false)} />

      <PortalCreateChatModal
        open={openCreateModal}
        onClose={onCloseCreateChatModal}
        onStartPrivateChat={onStartPrivateChat}
        onStartGroupChat={onStartGroupChat}
        setStep={setStep}
        step={step}
        creatingGroupChat={groupChat}
      />

      {!firstLoad && (
        <Box sx={{ position: "relative", overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ overflow: "hidden", overflowY: "auto", flex: 1, minHeight: 0 }}>
            {chats.map((chat, index) =>
              index !== chats.length - 1 ? (
                <Box key={index}>
                  <PortalChatListItem
                    setChats={setChats}
                    isSelected={selectedChat && selectedChat.externalId == chat.externalId}
                    onChatSelect={onChatSelect}
                    showResponseTimes={filters.showResponseTimes}
                    chat={chat}
                    userTyping={userTyping && chat.externalId === userTyping.conversation?.externalId ? userTyping : undefined}
                  />
                  <StyledDivider />
                </Box>
              ) : (
                <Box key={index} ref={lastChatListItemRef}>
                  <PortalChatListItem
                    setChats={setChats}
                    isSelected={selectedChat && selectedChat.externalId == chat.externalId}
                    onChatSelect={onChatSelect}
                    showResponseTimes={filters.showResponseTimes}
                    chat={chat}
                    userTyping={userTyping && chat.externalId === userTyping.conversation?.externalId ? userTyping : undefined}
                  />
                  <StyledDivider />
                </Box>
              )
            )}
          </Box>
          <LoadingIcon loading={loadingPage} />
        </Box>
      )}
      {!firstLoad && !loadingPage && chats.length === 0 && <EmptyChatList text={"No chats found"} />}
    </ChatListContainer>
  );
};

export function LoadingIcon({
  loading,
  rect,
}: {
  loading: boolean;
  rect?: { top?: number; bottom?: number; left?: number; right?: number };
}) {
  let coords: { top?: number; bottom?: number; left?: number; right?: number } = { bottom: 40 };
  const empty = {};

  if (rect) {
    coords = rect;
  }

  let transitionStyle: SxProps = {
    ...(loading ? coords : empty),
    transition: "all 0.125s ease-in",
  };

  return (
    <Box
      sx={{
        position: "absolute",
        opacity: loading ? 1 : 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        width: "100%",
        ...transitionStyle,
      }}
    >
      <Box
        sx={{
          padding: "0.25rem",
          border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
          borderRadius: "100px",
          backgroundColor: "#fff",
          width: "fit-content",
        }}
      >
        <Spinner size={"1.375rem"} sx={{ color: Colors.LAZO_PURPLE }} />
      </Box>
    </Box>
  );
}

const ChatListContainer = styled(Box)<{ isPortal: boolean }>(({ isPortal, theme }) => ({
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: isPortal ? "0" : "0.75rem, 1.5rem",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  "-ms-overflow-style": "none",
  [theme.breakpoints.down("sm")]: {
    padding: "0",
  },
}));

const StyledDivider = styled(Divider)(() => ({
  width: "100%",
}));

const ToolBar = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "sticky",
  top: 0,
  backgroundColor: "#fff",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  zIndex: theme.zIndex.appBar,
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "space-evenly",
  padding: "0.4rem 0.75rem",
  [theme.breakpoints.down("sm")]: {
    padding: "0.5rem",
    minHeight: "auto",
  },
}));

const SegmentedControl = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "50px",
  padding: "0.15rem",
  gap: "0.1rem",
}));

const SegmentOption = styled(Button, { shouldForwardProp: (prop) => prop !== "selected" })<{ selected: boolean }>(({ selected }) => ({
  color: selected ? Colors.LAZO_PURPLE : Colors.LIGHT_GRAY_8A8E93,
  padding: "0.15rem 0.65rem",
  margin: 0,
  border: "none",
  borderRadius: "50px",
  fontWeight: selected ? "600" : "500",
  fontSize: "0.75rem",
  textTransform: "none",
  whiteSpace: "nowrap",
  minHeight: "1.6rem",
  transition: "all 0.2s ease-out",
  backgroundColor: selected ? Colors.WHITE : "transparent",
  boxShadow: selected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
  ":hover": {
    backgroundColor: selected ? Colors.WHITE : Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    color: Colors.LAZO_PURPLE,
  },
}));

// StyledButton kept for potential external usage
const StyledButton = SegmentOption;

const StyledIconButton = styled(Button)(() => ({
  color: Colors.LAZO_PURPLE,
  padding: "0.15rem 0.75rem",
  margin: 0,
  border: `1.5px solid ${Colors.LAZO_PURPLE}`,
  borderRadius: "50px",
  fontWeight: "600",
  fontSize: "0.75rem",
  textTransform: "none",
  whiteSpace: "nowrap",
  minHeight: "1.6rem",
  transition: "all 0.2s ease-out",
  backgroundColor: Colors.LAZO_LIGHT_PURPLE,
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
}));
